import { useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  alpha,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import useApi from "../../../hooks/useApi";
import { useToast } from "../../../contexts/ToastContext";
import { useRegisterCadastrosPageActions } from "../layout";

const emptyFilters = {
  q: "",
  grupo: "",
  codigo: "",
};

const SORTABLE_COLUMNS = [
  { id: "B1_COD", label: "Código" },
  { id: "B1_DESC", label: "Descrição" },
  { id: "B1_TIPO", label: "Tipo" },
  { id: "B1_GRUPO", label: "Grupo" },
  { id: "B1_UM", label: "UM" },
  { id: "B1_POSIPI", label: "NCM" },
  { id: "DATA_INCLUSAO", label: "Incluído em" },
  { id: "USUARIO_INCLUSAO", label: "Incluído por" },
];

const findGrupoOption = (options, codigo) =>
  options.find((opt) => opt.codigo === codigo) || null;

const formatarDataInclusao = (value) => {
  if (!value) return "—";
  const raw = String(value).trim();
  const iso = raw.slice(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return raw;
  return `${match[3]}/${match[2]}/${match[1]}`;
};

const compareSortValues = (a, b, field) => {
  const va = a?.[field];
  const vb = b?.[field];
  if (va == null || va === "") return vb == null || vb === "" ? 0 : 1;
  if (vb == null || vb === "") return -1;
  if (field === "DATA_INCLUSAO") {
    return String(va).localeCompare(String(vb));
  }
  return String(va).localeCompare(String(vb), "pt-BR", {
    numeric: true,
    sensitivity: "base",
  });
};

const ConsultaProdutos = () => {
  const api = useApi();
  const { notifyError, notifySuccess } = useToast();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [filtros, setFiltros] = useState(emptyFilters);
  const [grupos, setGrupos] = useState([]);
  const [loadingGrupos, setLoadingGrupos] = useState(true);
  const [itens, setItens] = useState([]);
  const [selecionado, setSelecionado] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [consultou, setConsultou] = useState(false);
  const [filtrosExtraOpen, setFiltrosExtraOpen] = useState(false);
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");
  const debounceRef = useRef(null);

  const setFiltro = (key, value) => setFiltros((prev) => ({ ...prev, [key]: value }));

  const grupoSelecionado = useMemo(
    () => findGrupoOption(grupos, filtros.grupo),
    [grupos, filtros.grupo]
  );

  const filtrosAtivos = Boolean(filtros.grupo || filtros.codigo.trim());

  const itensOrdenados = useMemo(() => {
    if (!orderBy) return itens;
    const direction = order === "desc" ? -1 : 1;
    return [...itens].sort((a, b) => direction * compareSortValues(a, b, orderBy));
  }, [itens, orderBy, order]);

  const handleSort = (field) => {
    if (orderBy === field) {
      setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setOrderBy(field);
    setOrder("asc");
  };

  const carregarGrupos = async () => {
    setLoadingGrupos(true);
    try {
      const { data } = await api.get("Painel/API/Cadastros/Produtos/ListarOpcoes/index.php");
      setGrupos(data.data?.grupos || []);
    } catch (e) {
      notifyError(e?.response?.data?.message || "Erro ao carregar grupos.");
    } finally {
      setLoadingGrupos(false);
    }
  };

  useEffect(() => {
    carregarGrupos();
  }, []);

  const consultar = async (override = null, { silent = false } = {}) => {
    const atual = override || filtros;
    const q = atual.q.trim();
    const codigo = atual.codigo.trim();
    const grupo = (atual.grupo || "").trim();

    if (q.length < 3 && codigo.length < 2 && !grupo) {
      if (!silent) {
        notifyError("Informe descrição (mín. 3), código (mín. 2) e/ou um grupo.");
      }
      return;
    }

    setBuscando(true);
    try {
      const params = new URLSearchParams({ limit: "0" });
      if (q) params.set("q", q);
      if (codigo) params.set("codigo", codigo);
      if (grupo) params.set("grupo", grupo);

      const { data } = await api.get(
        `Painel/API/Cadastros/Produtos/ConsultarSB1/index.php?${params.toString()}`
      );
      const lista = data.data || [];
      setItens(lista);
      setConsultou(true);
      setSelecionado((prev) => {
        if (!prev) return lista[0] || null;
        return lista.find((item) => item.B1_COD === prev.B1_COD) || lista[0] || null;
      });
      if (!silent && lista.length === 0) {
        notifySuccess("Nenhum produto encontrado na SB1 com esses filtros.");
      }
    } catch (e) {
      if (!silent) {
        notifyError(e?.response?.data?.message || "Erro ao localizar produtos na SB1.");
      }
    } finally {
      setBuscando(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = filtros.q.trim();
    const codigo = filtros.codigo.trim();
    const grupo = (filtros.grupo || "").trim();
    if (q.length < 3 && codigo.length < 2 && !grupo) return undefined;

    debounceRef.current = setTimeout(() => {
      consultar(filtros, { silent: true });
    }, 550);

    return () => clearTimeout(debounceRef.current);
  }, [filtros.q, filtros.codigo, filtros.grupo]);

  useRegisterCadastrosPageActions({
    onRefresh: () => consultar(filtros),
    refreshing: buscando,
  });

  const copiarCodigo = async (codigo) => {
    try {
      await navigator.clipboard.writeText(codigo);
      notifySuccess(`Código ${codigo} copiado.`);
    } catch {
      notifyError("Não foi possível copiar o código.");
    }
  };

  const limpar = () => {
    setFiltros(emptyFilters);
    setItens([]);
    setSelecionado(null);
    setConsultou(false);
    setOrderBy("");
    setOrder("asc");
  };

  const renderFiltrosSecundarios = () => (
    <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
      <Autocomplete
        options={grupos}
        value={grupoSelecionado}
        loading={loadingGrupos}
        onChange={(_, option) => setFiltro("grupo", option?.codigo || "")}
        getOptionLabel={(option) => option?.label || option?.codigo || ""}
        isOptionEqualToValue={(option, value) => option.codigo === value.codigo}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Grupo"
            placeholder="Todos os grupos"
            helperText={filtros.grupo ? `Código: ${filtros.grupo}` : "Opcional"}
          />
        )}
        sx={{ minWidth: { md: 280 }, flex: 1 }}
        fullWidth
      />
      <TextField
        label="Código B1"
        value={filtros.codigo}
        onChange={(e) => setFiltro("codigo", e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && consultar()}
        placeholder="Ex.: MI184"
        sx={{ minWidth: { md: 220 } }}
        helperText="Mínimo 2 caracteres"
        fullWidth
      />
    </Stack>
  );

  return (
    <Box
      sx={{
        p: { xs: 1.5, md: 2.5 },
        height: "100%",
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
        <Typography variant="body2" color="text.secondary" sx={{ display: { xs: "none", md: "block" } }}>
          Localize e valide o produto no Protheus (SB1). A busca ignora maiúsculas e acentos.
        </Typography>

        <Paper
          variant="outlined"
          sx={(t) => ({
            p: { xs: 1.5, md: 2 },
            borderRadius: 2,
            position: "sticky",
            top: 0,
            zIndex: 2,
            bgcolor: alpha(t.palette.background.paper, 0.96),
            backdropFilter: "blur(8px)",
          })}
        >
          <Stack spacing={1.5}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "flex-start" }}>
              <TextField
                label="Descrição / palavras-chave"
                value={filtros.q}
                onChange={(e) => setFiltro("q", e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && consultar()}
                placeholder="Ex.: parafuso sextavado M8"
                fullWidth
                helperText="Mínimo 3 caracteres"
                InputProps={{
                  sx: { minHeight: 44 },
                }}
              />
              <Stack
                direction="row"
                spacing={1}
                sx={{ width: { xs: "100%", sm: "auto" }, flexShrink: 0, pt: { sm: 0.5 } }}
              >
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={
                    buscando ? <CircularProgress size={16} color="inherit" /> : <SearchOutlinedIcon />
                  }
                  onClick={() => consultar()}
                  disabled={buscando}
                  sx={{ minHeight: 44, flex: { xs: 1, sm: "none" }, px: 2.5 }}
                >
                  {buscando ? "…" : "Localizar"}
                </Button>
                <Button
                  variant="outlined"
                  onClick={limpar}
                  disabled={buscando}
                  sx={{ minHeight: 44, display: { xs: "none", sm: "inline-flex" } }}
                >
                  Limpar
                </Button>
              </Stack>
            </Stack>

            {isMobile ? (
              <Accordion
                disableGutters
                elevation={0}
                expanded={filtrosExtraOpen}
                onChange={(_, open) => setFiltrosExtraOpen(open)}
                sx={{
                  bgcolor: "transparent",
                  "&:before": { display: "none" },
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "10px !important",
                  overflow: "hidden",
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44, px: 1.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TuneOutlinedIcon fontSize="small" color="action" />
                    <Typography variant="body2" fontWeight={600}>
                      Mais filtros
                    </Typography>
                    {filtrosAtivos && (
                      <Chip size="small" color="secondary" label="Ativos" sx={{ height: 22 }} />
                    )}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, px: 1.5, pb: 1.5 }}>
                  {renderFiltrosSecundarios()}
                  <Button
                    variant="text"
                    size="small"
                    onClick={limpar}
                    disabled={buscando}
                    sx={{ mt: 1, minHeight: 40 }}
                  >
                    Limpar filtros
                  </Button>
                </AccordionDetails>
              </Accordion>
            ) : (
              renderFiltrosSecundarios()
            )}
          </Stack>
        </Paper>

        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={2}
          alignItems="stretch"
          sx={{ flex: 1, minHeight: 0 }}
        >
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 1.5, md: 2 },
              borderRadius: 2,
              flex: 1.4,
              minHeight: { xs: "auto", lg: 280 },
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
              <Typography variant="subtitle2" fontWeight={700}>
                Resultados
              </Typography>
              {consultou && (
                <Chip
                  size="small"
                  label={`${itens.length} item(ns)`}
                  color={itens.length ? "success" : "default"}
                />
              )}
            </Stack>

            {!consultou && (
              <Alert severity="info">Faça uma busca para localizar produtos na SB1.</Alert>
            )}
            {consultou && itens.length === 0 && (
              <Alert severity="warning">Nenhum produto encontrado. Confira os termos da busca.</Alert>
            )}

            {itensOrdenados.length > 0 && isMobile && (
              <Stack spacing={1} sx={{ overflow: "auto", flex: 1, minHeight: 0, pb: 1 }}>
                {itensOrdenados.map((item) => {
                  const ativo = selecionado?.B1_COD === item.B1_COD;
                  return (
                    <Box
                      key={item.B1_COD}
                      onClick={() => setSelecionado(item)}
                      sx={(t) => ({
                        p: 1.5,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: ativo ? "secondary.main" : "divider",
                        bgcolor: ativo
                          ? alpha(t.palette.secondary.main, 0.1)
                          : "background.paper",
                        cursor: "pointer",
                        transition: "border-color 0.15s ease, background-color 0.15s ease",
                        "&:active": { transform: "scale(0.99)" },
                      })}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="body2" fontWeight={800} letterSpacing={0.2}>
                            {item.B1_COD}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                            {item.B1_DESC}
                          </Typography>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                            {item.B1_TIPO && <Chip size="small" label={`Tipo ${item.B1_TIPO}`} />}
                            {item.B1_GRUPO && <Chip size="small" label={`Grupo ${item.B1_GRUPO}`} />}
                            {item.B1_UM && <Chip size="small" label={item.B1_UM} variant="outlined" />}
                          </Stack>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                            Incluído em {formatarDataInclusao(item.DATA_INCLUSAO)}
                            {item.USUARIO_INCLUSAO ? ` por ${item.USUARIO_INCLUSAO}` : ""}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          aria-label={`Copiar ${item.B1_COD}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            copiarCodigo(item.B1_COD);
                          }}
                          sx={{ minWidth: 40, minHeight: 40 }}
                        >
                          <ContentCopyOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}

            {itensOrdenados.length > 0 && !isMobile && (
              <TableContainer sx={{ maxHeight: 520, flex: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {SORTABLE_COLUMNS.map((col) => (
                        <TableCell
                          key={col.id}
                          sortDirection={orderBy === col.id ? order : false}
                          sx={{ whiteSpace: "nowrap" }}
                        >
                          <TableSortLabel
                            active={orderBy === col.id}
                            direction={orderBy === col.id ? order : "asc"}
                            onClick={() => handleSort(col.id)}
                          >
                            {col.label}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {itensOrdenados.map((item) => {
                      const ativo = selecionado?.B1_COD === item.B1_COD;
                      return (
                        <TableRow
                          key={item.B1_COD}
                          hover
                          selected={ativo}
                          onClick={() => setSelecionado(item)}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight={700}>
                              {item.B1_COD}
                            </Typography>
                          </TableCell>
                          <TableCell>{item.B1_DESC}</TableCell>
                          <TableCell>{item.B1_TIPO || "—"}</TableCell>
                          <TableCell>{item.B1_GRUPO || "—"}</TableCell>
                          <TableCell>{item.B1_UM || "—"}</TableCell>
                          <TableCell>{item.B1_POSIPI || "—"}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {formatarDataInclusao(item.DATA_INCLUSAO)}
                          </TableCell>
                          <TableCell>{item.USUARIO_INCLUSAO || "—"}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              p: { xs: 1.5, md: 2 },
              borderRadius: 2,
              flex: 1,
              minHeight: { xs: "auto", lg: selecionado ? 280 : "auto" },
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
              Validação do produto
            </Typography>
            {!selecionado ? (
              <Typography variant="body2" color="text.secondary">
                Selecione um item na lista para revisar os dados e confirmar se é o produto correto.
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                <Alert severity="success" sx={{ py: 0.5 }}>
                  Produto localizado na SB1. Valide descrição, tipo e grupo.
                </Alert>
                <Field label="Código B1" value={selecionado.B1_COD} strong />
                <Field label="Descrição" value={selecionado.B1_DESC} />
                <Divider />
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  sx={{ display: { xs: "none", md: "flex" } }}
                >
                  <Field label="Tipo" value={selecionado.B1_TIPO} />
                  <Field
                    label="Grupo"
                    value={
                      selecionado.B1_GRUPO_DESC
                        ? `${selecionado.B1_GRUPO} — ${selecionado.B1_GRUPO_DESC}`
                        : selecionado.B1_GRUPO
                    }
                  />
                </Stack>
                <Box sx={{ display: { xs: "block", md: "none" } }}>
                  <Field label="Tipo" value={selecionado.B1_TIPO} />
                </Box>
                <Box sx={{ display: { xs: "block", md: "none" } }}>
                  <Field
                    label="Grupo"
                    value={
                      selecionado.B1_GRUPO_DESC
                        ? `${selecionado.B1_GRUPO} — ${selecionado.B1_GRUPO_DESC}`
                        : selecionado.B1_GRUPO
                    }
                  />
                </Box>
                <Field label="Unidade de medida" value={selecionado.B1_UM} />
                <Field label="NCM" value={selecionado.B1_POSIPI} />
                <Divider />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <Field label="Incluído em" value={formatarDataInclusao(selecionado.DATA_INCLUSAO)} />
                  <Field label="Incluído por" value={selecionado.USUARIO_INCLUSAO} />
                </Stack>
                <Box pt={0.5}>
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<ContentCopyOutlinedIcon />}
                    onClick={() => copiarCodigo(selecionado.B1_COD)}
                    fullWidth={isMobile}
                    sx={{ minHeight: 44 }}
                  >
                    Copiar código
                  </Button>
                </Box>
              </Stack>
            )}
          </Paper>
        </Stack>
      </Stack>
    </Box>
  );
};

const Field = ({ label, value, strong = false }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={strong ? 700 : 400} sx={{ whiteSpace: "pre-wrap" }}>
      {value || "—"}
    </Typography>
  </Box>
);

export default ConsultaProdutos;
